import { useState, useEffect, useMemo } from 'react';
import { Listbox, RadioGroup } from '@headlessui/react';
import {
	CheckIcon,
	ChevronUpDownIcon,
	BuildingOfficeIcon,
	UserIcon,
} from '@heroicons/react/24/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { StackableModal } from '../components/nextgen';
import { classNames, getUserFullName } from '../components/helpers/functions';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import {
	addContact,
	updateContact,
	getContacts,
	getContact,
} from '../api/contacts';

const MODAL_NAME = 'contactModal';

const TITLE_OPTIONS = [
	{ label: 'Field Engineer', value: 'Field Engineer' },
	{ label: 'Support', value: 'Support' },
	{ label: 'Service Manager', value: 'Service Manager' },
	{ label: 'Sales', value: 'Sales' },
	{ label: 'Finance', value: 'Finance' },
	{ label: 'Executive', value: 'Executive' },
];

const ContactModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);

	const isEditing = !!open?.id;
	const onSuccess = open?.onSuccess;

	// Form state
	const [contactType, setContactType] = useState('person');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [companyName, setCompanyName] = useState('');
	const [email, setEmail] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [location, setLocation] = useState('');
	const [title, setTitle] = useState('');
	const [customTitle, setCustomTitle] = useState('');
	const [companyId, setCompanyId] = useState(null);
	const [createNewCompany, setCreateNewCompany] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState(null);

	// Fetch existing contacts for company dropdown
	const { value: contacts, status: isLoadingContacts } = useAsync(getContacts, {
		defaultPayload: { pageSize: null },
	});

	// Fetch contact details when editing
	const {
		value: contactDetails,
		execute: fetchContact,
		status: isLoadingContact,
	} = useAsync(getContact, { immediate: false });

	// Get unique companies from contacts
	const companyOptions = useMemo(() => {
		if (!contacts?.rows) return [];
		return contacts.rows
			.filter((c) => c.type === 'company')
			.map((c) => ({
				label: c.companyName,
				value: c.id,
			}))
			.filter(
				(item, index, self) =>
					index === self.findIndex((t) => t.value === item.value),
			);
	}, [contacts]);

	// Reset form when modal opens
	useEffect(() => {
		if (open) {
			if (open.id) {
				// Editing mode - fetch contact details
				fetchContact({ id: open.id });
			} else {
				// Create mode - reset form
				setContactType(open.type || 'person');
				setFirstName('');
				setLastName('');
				setCompanyName('');
				setEmail('');
				setPhoneNumber('');
				setLocation('');
				setTitle('');
				setCustomTitle('');
				setCompanyId(null);
				setCreateNewCompany('');
				setError(null);
			}
		}
	}, [open, fetchContact]);

	// Populate form when contact details are loaded
	useEffect(() => {
		if (contactDetails && isEditing) {
			setContactType(contactDetails.type || 'person');
			setFirstName(contactDetails.firstName || '');
			setLastName(contactDetails.lastName || '');
			setCompanyName(contactDetails.companyName || '');
			setEmail(contactDetails.email || '');
			setPhoneNumber(contactDetails.phoneNumber || '');
			setLocation(contactDetails.location || '');

			// Handle title
			const existingTitle = TITLE_OPTIONS.find(
				(t) => t.value === contactDetails.title,
			);
			if (existingTitle) {
				setTitle(contactDetails.title);
				setCustomTitle('');
			} else if (contactDetails.title) {
				setTitle('custom');
				setCustomTitle(contactDetails.title);
			} else {
				setTitle('');
				setCustomTitle('');
			}

			setCompanyId(contactDetails.company?.id || null);
			setCreateNewCompany('');
			setError(null);
		}
	}, [contactDetails, isEditing]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validation
		if (contactType === 'company' && !companyName.trim()) {
			setError('Company name is required');
			return;
		}

		if (contactType === 'person' && !email.trim()) {
			setError('Email is required');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const payload = {
				type: contactType,
				email: email.trim() || null,
				phoneNumber: phoneNumber.trim() || null,
				location: location.trim() || null,
			};

			if (contactType === 'company') {
				payload.companyName = companyName.trim();
			} else {
				payload.firstName = firstName.trim() || null;
				payload.lastName = lastName.trim() || null;
				payload.title =
					title === 'custom' ? customTitle.trim() : title || null;

				if (createNewCompany.trim()) {
					payload.createNewCompany = createNewCompany.trim();
				} else if (companyId) {
					payload.companyId = companyId;
				} else {
					payload.companyId = null;
				}
			}

			if (isEditing) {
				await updateContact({ id: open.id, ...payload });
			} else {
				await addContact(payload);
			}

			if (onSuccess) {
				onSuccess();
			}
			setClose();
		} catch (err) {
			setError(err.message || 'Failed to save contact');
		} finally {
			setIsSubmitting(false);
		}
	};

	const selectedCompany = companyOptions.find((c) => c.value === companyId);
	const selectedTitle = TITLE_OPTIONS.find((t) => t.value === title);

	const isLoading = isLoadingContact === 'pending';

	return (
		<StackableModal open={!!open} onClose={() => setClose()} size="md">
			<form onSubmit={handleSubmit} className="space-y-4">
				<div>
					<h2 className="text-lg font-semibold text-gray-900">
						{isEditing ? 'Edit Contact' : 'Create Contact'}
					</h2>
					<p className="text-sm text-gray-500 mt-1">
						{isEditing
							? 'Update the contact information below.'
							: 'Add a new contact to your directory.'}
					</p>
				</div>

				{error && (
					<div className="rounded-md bg-red-50 p-3">
						<p className="text-sm text-red-700">{error}</p>
					</div>
				)}

				{isLoading ? (
					<div className="py-8 text-center text-gray-500">
						Loading contact...
					</div>
				) : (
					<>
						{/* Contact Type Selection - only show for new contacts */}
						{!isEditing && (
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Contact Type
								</label>
								<RadioGroup value={contactType} onChange={setContactType}>
									<div className="grid grid-cols-2 gap-3">
										{[
											{
												value: 'person',
												label: 'Person',
												icon: UserIcon,
											},
											{
												value: 'company',
												label: 'Company',
												icon: BuildingOfficeIcon,
											},
										].map((type) => (
											<RadioGroup.Option
												key={type.value}
												value={type.value}
												className={({ checked, active }) =>
													classNames(
														checked
															? 'border-transparent ring-2 ring-indigo-500'
															: 'border-gray-300',
														active ? 'border-indigo-500' : '',
														'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none',
													)
												}
											>
												{({ checked }) => (
													<>
														<type.icon
															className={classNames(
																'h-5 w-5 mr-2',
																checked ? 'text-indigo-600' : 'text-gray-400',
															)}
														/>
														<span className="flex flex-1">
															<RadioGroup.Label
																as="span"
																className="block text-sm font-medium text-gray-900"
															>
																{type.label}
															</RadioGroup.Label>
														</span>
														<CheckCircleIcon
															className={classNames(
																!checked ? 'invisible' : '',
																'h-5 w-5 text-indigo-600',
															)}
														/>
													</>
												)}
											</RadioGroup.Option>
										))}
									</div>
								</RadioGroup>
							</div>
						)}

						{/* Company Name - only for company type */}
						{contactType === 'company' && (
							<div>
								<label
									htmlFor="companyName"
									className="block text-sm font-medium text-gray-700"
								>
									Company Name <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									id="companyName"
									value={companyName}
									onChange={(e) => setCompanyName(e.target.value)}
									placeholder="Enter company name"
									className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
								/>
							</div>
						)}

						{/* Person fields */}
						{contactType === 'person' && (
							<>
								{/* Name fields */}
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label
											htmlFor="firstName"
											className="block text-sm font-medium text-gray-700"
										>
											First Name
										</label>
										<input
											type="text"
											id="firstName"
											value={firstName}
											onChange={(e) => setFirstName(e.target.value)}
											placeholder="First name"
											className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
										/>
									</div>
									<div>
										<label
											htmlFor="lastName"
											className="block text-sm font-medium text-gray-700"
										>
											Last Name
										</label>
										<input
											type="text"
											id="lastName"
											value={lastName}
											onChange={(e) => setLastName(e.target.value)}
											placeholder="Last name"
											className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
										/>
									</div>
								</div>

								{/* Title */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Title
									</label>
									<Listbox
										value={title}
										onChange={(val) => {
											setTitle(val);
											if (val !== 'custom') setCustomTitle('');
										}}
									>
										<div className="relative">
											<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
												<span
													className={
														selectedTitle || title === 'custom'
															? ''
															: 'text-gray-500'
													}
												>
													{title === 'custom'
														? customTitle || 'Custom title'
														: selectedTitle?.label || 'Select title'}
												</span>
												<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												<Listbox.Option
													value=""
													className={({ active }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
														)
													}
												>
													<span className="text-gray-500">No title</span>
												</Listbox.Option>
												{TITLE_OPTIONS.map((option) => (
													<Listbox.Option
														key={option.value}
														value={option.value}
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div className="flex items-center justify-between">
																<span
																	className={selected ? 'font-semibold' : ''}
																>
																	{option.label}
																</span>
																{selected && (
																	<CheckIcon className="h-4 w-4 text-indigo-600" />
																)}
															</div>
														)}
													</Listbox.Option>
												))}
												<Listbox.Option
													value="custom"
													className={({ active, selected }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
															selected ? 'bg-indigo-100' : '',
														)
													}
												>
													{({ selected }) => (
														<div className="flex items-center justify-between">
															<span className={selected ? 'font-semibold' : ''}>
																Custom title...
															</span>
															{selected && (
																<CheckIcon className="h-4 w-4 text-indigo-600" />
															)}
														</div>
													)}
												</Listbox.Option>
											</Listbox.Options>
										</div>
									</Listbox>
									{title === 'custom' && (
										<input
											type="text"
											value={customTitle}
											onChange={(e) => setCustomTitle(e.target.value)}
											placeholder="Enter custom title"
											className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
										/>
									)}
								</div>

								{/* Company Selection */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Company
									</label>
									<Listbox
										value={companyId}
										onChange={(val) => {
											setCompanyId(val);
											if (val) setCreateNewCompany('');
										}}
										disabled={!!createNewCompany}
									>
										<div className="relative">
											<Listbox.Button
												className={classNames(
													'relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
													createNewCompany ? 'bg-gray-50' : '',
												)}
											>
												<span
													className={selectedCompany ? '' : 'text-gray-500'}
												>
													{selectedCompany?.label || 'Select company'}
												</span>
												<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												<Listbox.Option
													value={null}
													className={({ active }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
														)
													}
												>
													<span className="text-gray-500">No company</span>
												</Listbox.Option>
												{companyOptions.map((option) => (
													<Listbox.Option
														key={option.value}
														value={option.value}
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div className="flex items-center justify-between">
																<span
																	className={selected ? 'font-semibold' : ''}
																>
																	{option.label}
																</span>
																{selected && (
																	<CheckIcon className="h-4 w-4 text-indigo-600" />
																)}
															</div>
														)}
													</Listbox.Option>
												))}
											</Listbox.Options>
										</div>
									</Listbox>
									<div className="mt-2">
										<label className="block text-xs text-gray-500 mb-1">
											Or create a new company
										</label>
										<input
											type="text"
											value={createNewCompany}
											onChange={(e) => {
												setCreateNewCompany(e.target.value);
												if (e.target.value) setCompanyId(null);
											}}
											placeholder="New company name"
											className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
										/>
									</div>
								</div>
							</>
						)}

						{/* Email */}
						<div>
							<label
								htmlFor="email"
								className="block text-sm font-medium text-gray-700"
							>
								Email{' '}
								{contactType === 'person' && (
									<span className="text-red-500">*</span>
								)}
							</label>
							<input
								type="email"
								id="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="email@example.com"
								className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
							/>
						</div>

						{/* Phone and Location */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label
									htmlFor="phoneNumber"
									className="block text-sm font-medium text-gray-700"
								>
									Phone Number
								</label>
								<input
									type="tel"
									id="phoneNumber"
									value={phoneNumber}
									onChange={(e) => setPhoneNumber(e.target.value)}
									placeholder="+1 (555) 000-0000"
									className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
								/>
							</div>
							<div>
								<label
									htmlFor="location"
									className="block text-sm font-medium text-gray-700"
								>
									Location
								</label>
								<input
									type="text"
									id="location"
									value={location}
									onChange={(e) => setLocation(e.target.value)}
									placeholder="City, State"
									className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
								/>
							</div>
						</div>
					</>
				)}

				{/* Actions */}
				<div className="flex justify-end gap-3 pt-4">
					<button
						type="button"
						onClick={() => setClose()}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={isSubmitting || isLoading}
						className={classNames(
							'px-4 py-2 text-sm font-medium text-white rounded-md',
							isSubmitting || isLoading
								? 'bg-indigo-400 cursor-not-allowed'
								: 'bg-indigo-600 hover:bg-indigo-700',
						)}
					>
						{isSubmitting
							? isEditing
								? 'Saving...'
								: 'Creating...'
							: isEditing
								? 'Save Changes'
								: 'Create Contact'}
					</button>
				</div>
			</form>
		</StackableModal>
	);
};

export default ContactModal;
